import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Filter, Library as LibraryIcon, X } from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import { Reveal } from "../lib/motion";
import { api } from "../lib/api";
import {
    INDUSTRIES,
    CATEGORIES,
    findIndustry,
    findCategory,
} from "../data/libraryMeta";

export default function LibraryIndex() {
    const [items, setItems] = useState(null);
    const [industry, setIndustry] = useState(null);
    const [category, setCategory] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const params = {};
        if (industry) params.industry = industry;
        if (category) params.category = category;
        api.get("/library", { params })
            .then((r) => setItems(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Couldn't load the library."));
    }, [industry, category]);

    const lastUpdated = useMemo(() => {
        if (!items || !items.length) return null;
        const latest = items.reduce(
            (a, b) => (a.updated_at > b.updated_at ? a : b),
            items[0],
        );
        return latest.updated_at;
    }, [items]);

    function clearFilters() {
        setIndustry(null);
        setCategory(null);
    }

    return (
        <div className="min-h-screen bg-page text-ink" data-testid="library-index-page">
            <Nav />
            <main>
                <section className="relative overflow-hidden border-b border-ink/10 py-20 lg:py-28">
                    <div className="pointer-events-none absolute -top-16 -right-16 h-[480px] w-[480px] animate-blob rounded-full bg-coral/15 blur-3xl" />
                    <div className="relative mx-auto max-w-[1400px] px-5 lg:px-10">
                        <Reveal>
                            <div className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-coral">
                                <LibraryIcon className="h-4 w-4" />
                                The Small-Biz Library
                            </div>
                            <h1
                                className="font-display text-5xl font-black leading-[0.95] tracking-tight text-ink sm:text-6xl lg:text-[6rem]"
                                data-testid="library-headline"
                            >
                                Tips, tools & tax —{" "}
                                <span className="font-serif italic font-normal text-coral">
                                    for your industry.
                                </span>
                            </h1>
                            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/75 lg:text-lg">
                                A free, ever-growing library for small businesses in Aotearoa and
                                Australia. Marketing tips, AI tools, tax updates, discounts,
                                memberships — filtered to your industry. Updated fortnightly.
                            </p>
                            {lastUpdated ? (
                                <div className="mt-6 text-[11px] font-bold uppercase tracking-widest text-ink/50">
                                    Last update · {new Date(lastUpdated).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}
                                </div>
                            ) : null}
                        </Reveal>
                    </div>
                </section>

                {/* Filters */}
                <section className="sticky top-16 z-30 border-b border-ink/10 bg-page/95 py-5 backdrop-blur-md lg:top-20">
                    <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
                        <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-ink/55">
                            <Filter className="h-3.5 w-3.5" /> Filter
                            {(industry || category) && (
                                <button
                                    onClick={clearFilters}
                                    className="ml-3 inline-flex items-center gap-1 text-coral hover:underline"
                                    data-testid="library-clear-filters"
                                >
                                    <X className="h-3 w-3" /> Clear
                                </button>
                            )}
                        </div>
                        <div className="mb-3 flex flex-wrap gap-2">
                            {INDUSTRIES.map((i) => {
                                const active = industry === i.slug;
                                return (
                                    <button
                                        key={i.slug}
                                        onClick={() => setIndustry(active ? null : i.slug)}
                                        className={`border-2 px-3 py-1.5 font-display text-xs font-bold uppercase tracking-wider transition-all ${
                                            active
                                                ? "border-coral bg-coral text-white"
                                                : "border-ink/15 bg-white text-ink hover:border-coral"
                                        }`}
                                        data-testid={`library-filter-industry-${i.slug}`}
                                    >
                                        <span className="mr-1.5">{i.emoji}</span>
                                        {i.label}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {CATEGORIES.map((c) => {
                                const active = category === c.slug;
                                return (
                                    <button
                                        key={c.slug}
                                        onClick={() => setCategory(active ? null : c.slug)}
                                        className={`border-2 px-3 py-1.5 font-display text-[11px] font-bold uppercase tracking-widest transition-all ${
                                            active
                                                ? "border-ink bg-ink text-white"
                                                : "border-ink/15 bg-white text-ink hover:border-ink"
                                        }`}
                                        data-testid={`library-filter-category-${c.slug}`}
                                    >
                                        {c.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Grid */}
                <section
                    className="bg-page py-16 lg:py-24"
                    data-testid="library-grid-section"
                >
                    <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
                        {error && (
                            <div className="border-2 border-coral bg-coral/10 p-6 text-center text-coral">
                                {error}
                            </div>
                        )}

                        {items === null && !error && (
                            <div className="text-center text-sm uppercase tracking-widest text-ink/50">
                                Loading…
                            </div>
                        )}

                        {items && items.length === 0 && (
                            <div
                                className="border-2 border-ink/15 bg-white p-16 text-center"
                                data-testid="library-empty"
                            >
                                <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                                    // Nothing here yet
                                </div>
                                <div className="font-display text-2xl font-black text-ink lg:text-3xl">
                                    {industry || category
                                        ? "No resources match those filters — yet."
                                        : "The library is being built right now."}
                                </div>
                                <p className="mx-auto mt-4 max-w-md text-sm text-ink/70">
                                    New resources drop every fortnight. Pop back soon — or grab
                                    the free 7-day checklist below to get moving today.
                                </p>
                            </div>
                        )}

                        {items && items.length > 0 && (
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {items.map((r) => {
                                    const cat = findCategory(r.category);
                                    return (
                                        <Link
                                            key={r.id}
                                            to={`/library/${r.slug}`}
                                            className="group flex h-full flex-col justify-between border-2 border-ink/15 bg-white p-6 transition-all hover:-translate-y-1 hover:border-coral lg:p-7"
                                            data-testid={`library-card-${r.slug}`}
                                        >
                                            <div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                                                        {cat?.label || r.category}
                                                    </span>
                                                    <ArrowUpRight className="h-4 w-4 text-ink/45 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-coral" />
                                                </div>
                                                <h3 className="mt-4 font-display text-xl font-black leading-tight tracking-tight text-ink lg:text-2xl">
                                                    {r.title}
                                                </h3>
                                                <p className="mt-3 text-sm leading-relaxed text-ink/70">
                                                    {r.excerpt}
                                                </p>
                                            </div>
                                            <div className="mt-6 flex flex-wrap gap-1.5 border-t border-ink/10 pt-4">
                                                {r.industries.slice(0, 3).map((ind) => {
                                                    const meta = findIndustry(ind);
                                                    if (!meta) return null;
                                                    return (
                                                        <span
                                                            key={ind}
                                                            className="text-[10px] font-bold uppercase tracking-widest text-ink/55"
                                                        >
                                                            {meta.emoji} {meta.label}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
